<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    /**
     * Component is used to render the language selection page for the test.
     * @property {String} submitSelectionEndpoint endpoint for the language selection request
     * @property {String} defaultLocale default locale for the select input
     * @property {String[]} supportedLocales array of supported locales
     * @property {Object} jwtTokenHandler handler so that the request is authorized
     */
    import { __, getLanguageNativeName } from '@oat-sa-private/ui-core';
    import { Button, Dropdown } from '@oat-sa-private/ui-elements';
    import { Notification } from '@oat-sa-private/ui-components';
    import request from 'core/fetchRequest';

    let {
        submitSelectionEndpoint,
        defaultLocale,
        supportedLocales = [],
        flagLanguageAccommodation = false,
        jwtTokenHandler,
        onSelected = () => {}
    } = $props();

    // svelte-ignore state_referenced_locally
    const languages = supportedLocales.reduce((prev, next) => {
        const languageNativeName = getLanguageNativeName(next);
        if (languageNativeName) {
            prev[next] = languageNativeName;
        }
        return prev;
    }, {});
    // svelte-ignore state_referenced_locally
    let selectedLocale = $state(languages[defaultLocale] ? defaultLocale : supportedLocales[0]);
    let error = $state(null);
    let buttonDisabled = $state(false);

    async function handleClick() {
        buttonDisabled = true;
        error = null;
        try {
            await request(submitSelectionEndpoint, {
                method: 'PUT',
                body: JSON.stringify({
                    locale: selectedLocale
                }),
                headers: {
                    'Content-Type': 'application/json'
                },
                jwtTokenHandler
            });
            onSelected({ selectedLocale });
            // eslint-disable-next-line no-unused-vars
        } catch (e) {
            error = {
                title: __('Something went wrong.'),
                message: __('Please try again.')
            };
        } finally {
            buttonDisabled = false;
        }
    }
</script>

<style>
    main.main {
        width: 100%;
        height: 100%;

        display: flex;
        justify-content: center;
        align-items: center;
    }

    .selection-heading {
        align-self: flex-start;
        font-size: var(--fontsize-body);
        font-weight: var(--dir-typography-bold-weight, 700);
        margin-bottom: 2.5rem;
    }

    .layout-centered {
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;
        background-size: cover;
    }

    .container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        height: 100%;
        align-items: center;
    }

    .form-container {
        display: flex;
        align-items: center;
        gap: 1rem;
    }

    .error-container {
        width: 100%;
        margin-bottom: 1rem;
    }

    .interpreter-notification {
        width: 100%;
        margin-top: 2.5rem;
    }

    .dropdown-container {
        width: 44rem;
    }

    .button-container {
        width: 12.25rem;
    }
</style>

<div class="layout-centered">
    <main class="main">
        <div class="container">
            <h3 class="selection-heading">{__('Please select a language')}</h3>
            {#if error}
                <div class="error-container">
                    <Notification hierarchy="alert" title={error.title} message={error.message} />
                </div>
            {/if}
            <div class="form-container">
                <div class="dropdown-container">
                    <Dropdown
                        fullwidth={true}
                        value={selectedLocale}
                        options={languages}
                        required={true}
                        reset={false}
                        on:change={e => {
                            selectedLocale = e.detail.value;
                        }} />
                </div>
                <div class="button-container">
                    <Button
                        fullwidth={true}
                        shape="pill"
                        size="small"
                        label={__('Select')}
                        on:click={handleClick}
                        disabled={buttonDisabled} />
                </div>
            </div>
            {#if flagLanguageAccommodation}
                <div class="interpreter-notification">
                    <Notification message={__('Interpreter assistance is being provided during this test.')} />
                </div>
            {/if}
        </div>
    </main>
</div>
